import PropTypes from "prop-types";
import {
  ExternalLink,
  File,
  FileText,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Upload,
  Video,
} from "lucide-react";
import { isFileObject } from "utils/uploadFormItems";
import { cn } from "lib/utils";

function getSafeFileName(input, fallbackName) {
  if (!input) {
    const fb = String(fallbackName ?? "").trim();
    return fb || "file";
  }
  if (typeof input === "string") {
    const withoutQuery = input.split("?")[0];
    const parts = withoutQuery.split("/");
    return parts.at(-1) || "file";
  }

  if (input.name) return input.name;
  if (input.url) return getSafeFileName(input.url);
  return "file";
}

function getFileKind(fileLike, displayName) {
  const fileName = getSafeFileName(fileLike, displayName);
  const extension = fileName.split(".").pop()?.toLowerCase() || "";
  const type = typeof fileLike === "object" ? fileLike?.type || "" : "";

  if (type.includes("pdf") || extension === "pdf") {
    return { label: "PDF", Icon: FileText };
  }

  if (
    type.startsWith("image/") ||
    ["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp"].includes(extension)
  ) {
    return { label: extension.toUpperCase() || "IMAGE", Icon: ImageIcon };
  }

  if (
    type.startsWith("video/") ||
    ["mp4", "mov", "avi", "mkv", "webm", "m4v"].includes(extension)
  ) {
    return { label: extension.toUpperCase() || "VIDEO", Icon: Video };
  }

  return { label: extension.toUpperCase() || "FILE", Icon: File };
}

function getItemFileLike(item) {
  if (!item) return null;
  if (typeof item.file === "string") return item.file;
  if (item.file) return item.file;
  if (item.url) return item.url;
  return null;
}

function getItemIdentifier(item, index) {
  return item?.id || item?.url || item?.file?.name || `upload-item-${index}`;
}

function uploadStatusLabel(status) {
  if (status === "complete") return "Complete";
  if (status === "uploading") return "Uploading";
  return "Ready";
}

export default function FileUploadPreviewList({
  items,
  showActions = true,
  showProgress = true,
  onUpload,
  onOpen,
  onDelete,
}) {
  if (!items?.length) return null;

  return (
    <div className="mt-4 space-y-3">
      {items.map((item, index) => {
        const fileLike = getItemFileLike(item);
        const displayName = item?.name;
        const fileName = getSafeFileName(fileLike, displayName);
        const itemId = getItemIdentifier(item, index);
        const { label, Icon } = getFileKind(fileLike, displayName);

        const hasLocalFileToUpload = isFileObject(item?.file);
        const isUploading = item?.status === "uploading";

        const showUploadTrigger = onUpload && hasLocalFileToUpload && !isUploading;
        const showUploadProgressControl = onUpload && hasLocalFileToUpload && isUploading;

        return (
          <div key={itemId} className="rounded-lg border border-border bg-card p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{fileName}</p>
                  <p className="text-xs text-muted-foreground">{label}</p>
                </div>
              </div>

              {showActions ? (
                <div className="flex items-center gap-1">
                  {showUploadProgressControl ? (
                    <span
                      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2 py-1.5 text-xs font-medium text-muted-foreground"
                      title="Uploading…"
                      aria-live="polite"
                      aria-busy="true"
                    >
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span className="sr-only">Uploading file</span>
                    </span>
                  ) : null}
                  {showUploadTrigger ? (
                    <button
                      type="button"
                      title="Upload"
                      onClick={() => onUpload(itemId, item)}
                      className="rounded-md border border-border p-2 text-foreground transition hover:bg-muted"
                    >
                      <Upload className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                  {onOpen ? (
                    <button
                      type="button"
                      title="Open"
                      onClick={() => onOpen(item)}
                      className="rounded-md border border-border p-2 text-foreground transition hover:bg-muted"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                  {onDelete ? (
                    <button
                      type="button"
                      title="Delete"
                      onClick={() => onDelete(itemId, item)}
                      className="rounded-md border border-border p-2 text-destructive transition hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>

            {showProgress ? (
              <div className="mt-3">
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">{uploadStatusLabel(item.status)}</span>
                  <span className="font-medium text-foreground">{item.progress || 0}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn(
                      "h-full rounded-full bg-accent transition-all",
                      item.status === "complete" && "bg-accent"
                    )}
                    style={{ width: `${item.progress || 0}%` }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

FileUploadPreviewList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      file: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          type: PropTypes.string,
        }),
      ]),
      url: PropTypes.string,
      status: PropTypes.string,
      progress: PropTypes.number,
    })
  ),
  showActions: PropTypes.bool,
  showProgress: PropTypes.bool,
  onUpload: PropTypes.func,
  onOpen: PropTypes.func,
  onDelete: PropTypes.func,
};
